import { useCallback, useRef, useState } from 'react';
import { useAppStore } from '../../store/appStore';
import type { TaskDetailKind } from '../../navigation/screenIds';
import { createTaskDetailScreenModel } from './TaskDetailScreenModelScreen';
import { TaskDetailScreenController } from './TaskDetailScreenController';
import { TaskDetailScreenView } from './TaskDetailScreenView';

export type TaskDetailScreenContainerProps = {
  taskKind: TaskDetailKind;
  onGoBack?: () => void;
  /** Called after a successful daily-login claim with the awarded passes. */
  onDailyLoginClaimed?: (rewardPasses: number) => void;
  /** Called after a successful one-shot VK/business claim. */
  onTaskCompleted?: () => void;
};

export function TaskDetailScreen({
  taskKind,
  onGoBack,
  onDailyLoginClaimed,
  onTaskCompleted,
}: TaskDetailScreenContainerProps) {
  const playerEconomy = useAppStore((s) => s.playerEconomy);
  const reportActionError = useAppStore((s) => s.reportActionError);
  const [actionInFlight, setActionInFlight] = useState(false);
  const inFlightRef = useRef(false);

  const handleClaim = useCallback(() => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setActionInFlight(true);

    const finish = () => {
      inFlightRef.current = false;
      setActionInFlight(false);
    };

    if (taskKind === 'daily_login') {
      void useAppStore
        .getState()
        .claimDailyLoginReward()
        .then((result) => {
          if (result.alreadyClaimed || result.rewardPasses <= 0) {
            reportActionError('already_claimed');
            return;
          }
          onDailyLoginClaimed?.(result.rewardPasses);
        })
        .finally(finish);
      return;
    }

    if (taskKind === 'notifications') {
      void useAppStore
        .getState()
        .completeNotificationsTask()
        .then((result) => {
          if (result.completed && result.rewardPasses > 0) {
            onTaskCompleted?.();
          }
        })
        .finally(finish);
      return;
    }

    void useAppStore
      .getState()
      .completeVkSubscribeTask()
      .then((result) => {
        if (result.completed && result.rewardPasses > 0) {
          onTaskCompleted?.();
        }
      })
      .finally(finish);
  }, [taskKind, onDailyLoginClaimed, onTaskCompleted, reportActionError]);

  const model = createTaskDetailScreenModel({
    taskKind,
    playerEconomy,
    actionInFlight,
    onGoBack,
    onClaim: handleClaim,
  });
  const controller = new TaskDetailScreenController(model);

  return <TaskDetailScreenView model={model} controller={controller} />;
}
